import { useSyncExternalStore } from "react";
import { AKZENT_VORGABE, akzentRollen, type Akzentrollen } from "@mrmachine/fach";

/* ══════════════════════════════════════════════════════════════════
   Das Erscheinungsbild zur Laufzeit — Signal, Paket 6 (07.10.2026).

   Luca: Die Akzentfarbe soll im Einstellungsmenü gesetzt werden können,
   das Logo austauschbar sein. Seit Paket 1 hängt alles Gelbe an
   `--akzent` (packages/ui/src/tokens.css); hier wird dieser eine Wert —
   und `--auf-akzent` daneben — zur Laufzeit auf `<html>` gesetzt.

   **Kein Aufblitzen der Vorgabe.** Der zuletzt gesehene Stand liegt im
   Browser (`SPEICHER`), und das Kopfskript in `index.html` setzt ihn vor
   dem ersten Bild. Danach fragt `erscheinungsbildStarten()` den Server
   (offen, ohne Anmeldung — die Anmeldeseite braucht ihn auch) und zieht
   nach, wenn sich etwas geändert hat.

   **Der Ring.** Ist der Akzent gegen die Anthrazit-Leiste schwächer als
   3 : 1, verschwände der mittige Knopf darin; `data-akzent-ring` an
   `<html>` gibt ihm einen hellen Ring (`stile/signal-telefon.css`). Die
   Rechnung steht einmal in `@mrmachine/fach` (`akzent.ts`).

   **Die Logos.** URLs aus der Ablage, signiert. `null` heißt: das
   eingebaute Logo aus `public/`. Eine abgelaufene URL fällt im `<img>`
   über `onError` auf das eingebaute zurück (`LogoBild`).
   ══════════════════════════════════════════════════════════════════ */

export type Logoart = "hell" | "dunkel" | "bildmarke";

export type Erscheinungsbild = {
  /** Die gesetzte Farbe — `null`, solange die Vorgabe gilt. */
  akzent: string | null;
  rollen: Akzentrollen;
  logos: Record<Logoart, string | null>;
  eigene_logos: Record<Logoart, boolean>;
  vorschlaege: { id: string; hex: string; wort: string }[];
};

/** Derselbe Schlüssel wie im Kopfskript in `index.html`. */
export const SPEICHER = "machine-office:erscheinungsbild";

/** Die eingebauten Logos — was gilt, solange der Betrieb keins hochgeladen hat. */
export const EINGEBAUT: Record<Logoart | "kopf", string> = {
  hell: "/logo-hell.png",
  dunkel: "/logo-dunkel.png",
  kopf: "/logo-kopf-dunkel.png",
  bildmarke: "/logo-bildmarke.png",
};

function vorgabe(): Erscheinungsbild {
  return {
    akzent: null,
    rollen: akzentRollen(AKZENT_VORGABE),
    logos: { hell: null, dunkel: null, bildmarke: null },
    eigene_logos: { hell: false, dunkel: false, bildmarke: false },
    vorschlaege: [],
  };
}

function ausSpeicher(): Erscheinungsbild {
  try {
    const roh = localStorage.getItem(SPEICHER);
    if (!roh) return vorgabe();
    const w = JSON.parse(roh) as Partial<Erscheinungsbild>;
    const akzent = typeof w.akzent === "string" ? w.akzent : null;
    return {
      ...vorgabe(),
      akzent,
      rollen: akzentRollen(akzent ?? AKZENT_VORGABE),
      logos: { ...vorgabe().logos, ...(w.logos ?? {}) },
      eigene_logos: { ...vorgabe().eigene_logos, ...(w.eigene_logos ?? {}) },
    };
  } catch {
    return vorgabe();
  }
}

let stand: Erscheinungsbild = vorgabe();
const hoerer = new Set<() => void>();

/** Setzt die Variablen auf `<html>` — oder nimmt sie weg, wenn die Vorgabe gilt. */
export function anwenden(akzent: string | null): void {
  const wurzel = document.documentElement;
  if (!akzent) {
    wurzel.style.removeProperty("--akzent");
    wurzel.style.removeProperty("--auf-akzent");
    wurzel.removeAttribute("data-akzent-ring");
    return;
  }
  const r = akzentRollen(akzent);
  wurzel.style.setProperty("--akzent", r.akzent);
  wurzel.style.setProperty("--auf-akzent", r.auf);
  if (r.ring) wurzel.setAttribute("data-akzent-ring", "");
  else wurzel.removeAttribute("data-akzent-ring");
}

/** Den neuen Stand übernehmen: anwenden, merken, Masken benachrichtigen. */
export function erscheinungsbildSetzen(neu: Erscheinungsbild): void {
  stand = neu;
  anwenden(neu.akzent);
  try {
    localStorage.setItem(SPEICHER, JSON.stringify({
      akzent: neu.akzent, auf: neu.rollen.auf, ring: neu.rollen.ring,
      logos: neu.logos, eigene_logos: neu.eigene_logos,
    }));
  } catch { /* privates Fenster: dann eben ohne Gedächtnis */ }
  hoerer.forEach((h) => h());
}

/** Beim Start: gemerkten Stand nehmen, dann den Server fragen. */
export function erscheinungsbildStarten(): void {
  stand = ausSpeicher();
  anwenden(stand.akzent);
  void erscheinungsbildNeuHolen();
}

/** Den Server fragen — offen, ohne Kontext. Ohne Netz bleibt der gemerkte Stand. */
export async function erscheinungsbildNeuHolen(): Promise<void> {
  try {
    const antwort = await fetch("/api/erscheinungsbild", { headers: { accept: "application/json" } });
    if (!antwort.ok) return;
    erscheinungsbildSetzen(await antwort.json() as Erscheinungsbild);
  } catch { /* ohne Netz: der gemerkte Stand gilt */ }
}

function abonnieren(h: () => void): () => void {
  hoerer.add(h);
  return () => hoerer.delete(h);
}

/** Der aktuelle Stand für eine Maske. */
export function useErscheinungsbild(): Erscheinungsbild {
  return useSyncExternalStore(abonnieren, () => stand, () => stand);
}

/** Die Quelle eines Logos: hochgeladen oder eingebaut. */
export function logoQuelle(bild: Erscheinungsbild, art: Logoart, eingebaut: string = EINGEBAUT[art]): string {
  return bild.logos[art] ?? eingebaut;
}
